import {
  ChangeDetectorRef,
  Component,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AssetGroupDto } from '@assetaccountant/api/models/asset-group.dto';
import { GetAssetResponse } from '@assetaccountant/api/models/assets/get-asset.response';
import { Feature } from '@assetaccountant/api/models/feature';
import { ListFieldsResponse } from '@assetaccountant/api/models/fields/list-fields.response';
import { RegisterObjectType } from '@assetaccountant/api/models/register-object-type';
import { Asset } from '@assetaccountant/assets/models/asset';
import {
  CreateNewAssetForm,
  EditOnlyAssetForm,
} from '@assetaccountant/assets/models/edit-asset-form';
import { Register, toUniversalDateString } from '@assetaccountant/core';
import { AssetGroupSelectorComponent } from '@assetaccountant/core/components/asset-group-selector/asset-group-selector.component';
import { RegisterData, RegisterParams } from '@assetaccountant/core/resolvers/register.resolver';
import { TypedActivatedRoute } from '@assetaccountant/core/utils/route-utils';
import { TypedFormGroup } from '@assetaccountant/core/utils/typed-forms';
import { map } from 'rxjs/operators';
import { QUANTITY_UNITS } from '../view-asset/dispose-quantity-modal.helper';

@Component({
  selector: 'app-edit-asset-details',
  templateUrl: './edit-asset-details.component.html',
  styleUrls: ['./edit-asset-details.component.scss'],
  standalone: false,
})
export class EditAssetDetailsComponent implements OnInit, OnDestroy {
  readonly Feature = Feature;
  readonly RegisterObjectType = RegisterObjectType;
  readonly QUANTITY_UNITS = QUANTITY_UNITS;

  @ViewChild(AssetGroupSelectorComponent) groupSelector?: AssetGroupSelectorComponent;

  @Input() asset: Asset;
  @Input() form: TypedFormGroup<EditOnlyAssetForm> | TypedFormGroup<CreateNewAssetForm>;
  @Input() isEditAsset = false;
  @Input() listFieldsResponse?: ListFieldsResponse;

  /** Parent asset for the new asset */
  @Input() parentAsset: GetAssetResponse | null;

  @Input() classificationDefaults?: { [key: string]: string };

  newAssetGroup: AssetGroupDto;

  get currentGroup() {
    return this.groupSelector?.group;
  }

  get isAutoCodeOn() {
    return this.register?.newAssetCodeTemplate;
  }

  get minReassessmentDate(): string | null {
    if (this.isNew() && this.asset.regimes.length > 0) {
      const acquisitionDate = toUniversalDateString(this.form.controls.acquisitionDate.value);
      let minDate: string | null = null;
      this.asset.regimes.forEach(r => {
        const effectiveDate = toUniversalDateString(r.effectiveDate);
        if (effectiveDate && (!acquisitionDate || effectiveDate > acquisitionDate)) {
          if (!minDate || minDate > effectiveDate) {
            minDate = effectiveDate;
          }
        }
      });
      return minDate;
    }
    return null;
  }

  register: Register;

  private subscriptions = [
    this.route.data.subscribe(({ register }) => {
      this.register = register;
    }),
  ];

  constructor(
    @Inject(ActivatedRoute) private route: TypedActivatedRoute<RegisterParams, RegisterData>,
    private cd: ChangeDetectorRef,
  ) {}

  isNew(): this is { isEditAsset: false; form: TypedFormGroup<CreateNewAssetForm> } {
    return !this.isEditAsset;
  }

  ngOnInit() {
    if (this.isNew()) {
      this.subscriptions.push(
        this.form.controls.acquisitionDate.valueChanges
          .pipe(
            // From the server it will look like 2013-04-05T06:07:08, so convert to 2013-04-05
            map(v => v && toUniversalDateString(v)),
          )
          .subscribe(newPurchaseDate => {
            if (newPurchaseDate) {
              /**
               * Only update firstUseDate if it untouched or pristine
               * or if it is less than the new acquisitionDate
               */
              const oldFirstUseDate =
                this.form.controls.firstUseDate.value &&
                toUniversalDateString(this.form.controls.firstUseDate.value);
              if (
                this.form.controls.firstUseDate.untouched ||
                this.form.controls.firstUseDate.pristine ||
                (oldFirstUseDate && oldFirstUseDate < newPurchaseDate)
              ) {
                this.form.controls.firstUseDate.setValue(newPurchaseDate);
                this.form.controls.firstUseDate.markAsUntouched();
                this.form.controls.firstUseDate.markAsPristine();
                this.cd.markForCheck();
              }
            }
          }),
      );
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }
}
